import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect } from 'react';
import { AlertContext } from '../contexts/alert-context';
import { companionStatusChangedPayloadSchema } from '../data/receiving-ws';
import { isTerminalStatus, type Upload } from '../data/upload';
import type { UploaderConnection } from '../data/uploader-connection';
import { companionConnectionsQueryOptions, uploadsQueryOptions } from '../query/query-options';
import { storeLayout } from '../router';
import { useStoreWebSocket } from '../websocket/use-store-websocket';
import { useActive } from './use-active';

export function useUploads() {
  const { addErrorAlert } = useContext(AlertContext);
  const { store, storeService } = storeLayout.useRouteContext();
  const active = useActive();

  const { data: uploads = [], isLoading } = useQuery({
    ...uploadsQueryOptions(store.id),
    queryFn: () => {
      return storeService.getUploadsByStore(store.id).catch((error: Error) => {
        addErrorAlert(error.message);
        throw error;
      });
    },
    // Poll while an upload is in flight, but only when the user is actually
    // here — an idle or hidden tab left open overnight should not keep polling.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (active && data?.some((u) => !isTerminalStatus(u.status))) {
        return 5_000;
      }

      return false;
    },
  });

  return {
    uploads,
    loading: uploads.length === 0 && isLoading,
  };
}

export function useCompanionConnections(): {
  connections: UploaderConnection[];
  loading: boolean;
} {
  const { addErrorAlert } = useContext(AlertContext);
  const { store, storeService } = storeLayout.useRouteContext();
  const active = useActive();
  const queryClient = useQueryClient();
  const { subscribe } = useStoreWebSocket();

  const { data: connections = [], isLoading } = useQuery({
    ...companionConnectionsQueryOptions(store.id),
    queryFn: () => {
      return storeService.getCompanionConnections(store.id).catch((error: Error) => {
        addErrorAlert(error.message);
        throw error;
      });
    },
    // WebSocket handles real-time updates; keep a slow fallback for reconnect
    // gaps, but only while the user is actually here.
    refetchInterval: active ? 5 * 60_000 : false,
  });

  useEffect(() => {
    return subscribe('companion_status_changed', companionStatusChangedPayloadSchema, () => {
      queryClient.invalidateQueries({
        queryKey: companionConnectionsQueryOptions(store.id).queryKey,
      });
    });
  }, [subscribe, queryClient, store.id]);

  return {
    connections,
    loading: isLoading,
  };
}

export function useTriggerUpload() {
  const { addErrorAlert } = useContext(AlertContext);
  const { store, storeService } = storeLayout.useRouteContext();
  const queryClient = useQueryClient();

  const { mutateAsync: triggerUpload, isPending } = useMutation({
    mutationFn: () => {
      return storeService.triggerUpload(store.id).catch((error: Error) => {
        addErrorAlert('Could not trigger upload. Please try again.');
        throw error;
      });
    },
    onSuccess: (newUpload: Upload) => {
      queryClient.setQueryData(['stores', store.id, 'uploads'], (oldUploads: Upload[] = []) => [
        newUpload,
        ...oldUploads,
      ]);
    },
  });

  return { triggerUpload, isPending };
}
